(() => {
  const PX = 6, PRINT_WIDTH_MM = 14, HEIGHT = PRINT_WIDTH_MM * PX, MAX_OBJECTS = 128;
  const MIN_LINE_MM = 1.5, MAX_LINE_MM = 3;
  const CONTACT_LIMIT_MM = 4;
  const bridgeLabels = { off: 'None', rings: 'Top and bottom lines', center: 'Center line', bar: 'Bottom ring', hatch: 'Diagonal strokes' };
  const inkNames = { '#09090a': 'Black', '#4157ff': 'Blue', '#e74132': 'Red', '#167548': 'Green', '#ffffff': 'White', '#f2c600': 'Yellow' };
  const custom = ['rmlId', 'rmlKind', 'fontKey', 'filename', 'sourcePng', 'removeWhite'];
  const symbolGroups = [
    ['Arrows', [['ArrowUp', 'Arrow up'], ['ArrowDown', 'Arrow down'], ['ArrowLeft', 'Arrow left'], ['ArrowRight', 'Arrow right'], ['MoveVertical', 'Vertical arrows'], ['MoveHorizontal', 'Horizontal arrows'], ['ChevronsUp', 'This side up'], ['Undo2', 'Return arrow']]],
    ['Packaging', [['Wine', 'Fragile'], ['Umbrella', 'Keep dry'], ['Package', 'Package'], ['PackageCheck', 'Package checked'], ['Recycle', 'Recycle'], ['Leaf', 'Leaf'], ['Flame', 'Flame'], ['Snowflake', 'Snowflake']]],
    ['Signs', [['TriangleAlert', 'Caution'], ['OctagonAlert', 'Warning'], ['Check', 'Check'], ['X', 'Cross'], ['Plus', 'Plus'], ['Minus', 'Minus'], ['Copyright', 'Copyright'], ['Hash', 'Number sign']]],
    ['Shapes', [['Star', 'Star'], ['Heart', 'Heart'], ['Sun', 'Sun'], ['Moon', 'Moon'], ['Circle', 'Circle'], ['Square', 'Square'], ['Triangle', 'Triangle'], ['Diamond', 'Diamond']]],
  ];
  const symbols = Object.fromEntries(symbolGroups.flatMap(([, items]) => items));
  let editorCount = 0;
  const pdfBase = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@6.3.289/';
  let pdfLibrary;
  const renderPdfLogo = async (file) => {
    let pdfjs;
    try {
      pdfLibrary ||= import(`${pdfBase}build/pdf.min.mjs`).catch((error) => { pdfLibrary = null; throw error; });
      pdfjs = await pdfLibrary;
    } catch { throw new Error('The PDF reader could not load. Check your connection and try again.'); }
    pdfjs.GlobalWorkerOptions.workerSrc = `${pdfBase}build/pdf.worker.min.mjs`;
    const task = pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()),
      cMapUrl: `${pdfBase}cmaps/`, cMapPacked: true, standardFontDataUrl: `${pdfBase}standard_fonts/`,
      wasmUrl: `${pdfBase}wasm/`, iccUrl: `${pdfBase}iccs/`, enableXfa: false,
      stopAtErrors: true, canvasMaxAreaInBytes: 16777216 });
    let timedOut = false;
    const timeout = setTimeout(() => { timedOut = true; task.destroy().catch(() => {}); }, 45000);
    try {
      const pdf = await task.promise;
      if (pdf.numPages !== 1) throw new Error('Upload a single-page PDF containing your logo.');
      const page = await pdf.getPage(1), size = page.getViewport({ scale: 1 });
      if (!Number.isFinite(size.width) || !Number.isFinite(size.height) || size.width <= 0 || size.height <= 0) throw new Error('This PDF has an invalid page size.');
      const viewport = page.getViewport({ scale: 2048 / Math.max(size.width, size.height) });
      const source = document.createElement('canvas');
      source.width = Math.max(1, Math.ceil(viewport.width)); source.height = Math.max(1, Math.ceil(viewport.height));
      await page.render({ canvasContext: source.getContext('2d'), viewport, background: 'rgba(0,0,0,0)', intent: 'print' }).promise;
      return source;
    } catch (error) {
      if (timedOut) throw new Error('This PDF took too long to open. Try a simpler PDF or export your logo as SVG or PNG.');
      if (error.name === 'PasswordException') throw new Error('This PDF is password-protected. Upload an unlocked copy.');
      if (error.name === 'InvalidPDFException') throw new Error('This PDF could not be read. Export a fresh PDF and try again.');
      throw error;
    } finally { clearTimeout(timeout); await task.destroy().catch(() => {}); }
  };
  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
  const icon = (name) => `<i data-lucide="${name}" aria-hidden="true"></i>`;
  const tool = (name, label, glyph) => `<button type="button" class="rml-icon-button" data-edit="${name}" title="${label}" aria-label="${label}">${icon(glyph)}</button>`;

  // Match the cliche configurator: inspect ink columns, including the repeat seam.
  function contactGaps({ mask, w, h, ppm }, limit = CONTACT_LIMIT_MM) {
    const columns = new Uint8Array(w);
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) if (mask[y * w + x]) columns[x] = 1;
    const anchor = columns.indexOf(1), gaps = [];
    if (anchor < 0) return { gaps: [{ start: 0, length: w / ppm, parts: [[0, w / ppm]], wrap: false }], longest: w / ppm, empty: true };
    let start = -1, count = 0, longest = 0;
    const finish = () => {
      if (count / ppm > limit + 1e-8) {
        const end = start + count;
        gaps.push({ start: start / ppm, length: count / ppm, wrap: end > w,
          parts: end > w ? [[start / ppm, w / ppm], [0, (end - w) / ppm]] : [[start / ppm, end / ppm]] });
      }
      count = 0;
    };
    for (let j = 1; j <= w; j++) {
      const x = (anchor + j) % w;
      if (!columns[x]) { if (!count) start = x; count++; longest = Math.max(longest, count / ppm); }
      else finish();
    }
    return { gaps, longest, empty: false };
  }
  function letterClearance(gaps, thresholdMm) {
    const small = gaps.map(gap => gap.length).filter(length => length >= .35 && length <= Math.min(2.5, thresholdMm * .6)).sort((a, b) => a - b);
    return Math.max(.7, Math.min(1.5, small.length ? small[Math.floor(small.length / 2)] : 1));
  }
  function bridgeColumns(width, ppm, gaps, clearanceMm = 0, minSpanMm = .8) {
    const fillable = new Uint8Array(width);
    for (const gap of gaps) {
      const inset = Math.min(clearanceMm, Math.max(0, (gap.length - minSpanMm) / 2));
      const start = Math.round((gap.start + inset) * ppm), end = Math.round((gap.start + gap.length - inset) * ppm);
      for (let x = start; x < end; x++) fillable[x % width] = 1;
    }
    return fillable;
  }
  // The preview has different horizontal and vertical pixel densities; measure both in mm.
  function applyBridges(mask, width, height, ppm, config, fillable, verticalPpm = ppm) {
    if (config.mode !== 'positive' || config.bridge === 'off') return;
    const { bridge, bridgeWidth, bridgeGap, hatchAngle } = config;
    if (bridge === 'bar' || bridge === 'rings') {
      const rows = Math.min(height, Math.round(bridgeWidth * verticalPpm));
      mask.fill(1, (height - rows) * width);
      if (bridge === 'rings') mask.fill(1, 0, rows * width);
      return;
    }
    const heightMm = height / verticalPpm;
    for (let x = 0; x < width; x++) {
      if (!fillable[x]) continue;
      for (let y = Math.ceil(verticalPpm); y < height - verticalPpm; y++) {
        const ym = y / verticalPpm;
        let hit = false;
        if (bridge === 'center') hit = Math.abs(ym - heightMm / 2) < bridgeWidth / 2;
        else if (bridge === 'hatch') {
          let phase = (x / ppm - ym * Math.tan(hatchAngle * Math.PI / 180)) % bridgeGap;
          if (phase < 0) phase += bridgeGap;
          hit = phase < bridgeWidth;
        }
        if (hit) mask[y * width + x] = 1;
      }
    }
  }
  function normalizeBridges(state) {
    if (!Object.hasOwn(bridgeLabels, state.bridge)) state.bridge = state.lines ? 'rings' : 'off';
    state.lines = state.bridge === 'rings';
    state.lineMm = clamp(Number(state.lineMm) || MIN_LINE_MM, MIN_LINE_MM, MAX_LINE_MM);
    state.bridgeGap = clamp(Number(state.bridgeGap) || 3, 1, 10);
    state.hatchAngle = clamp(Number.isFinite(Number(state.hatchAngle)) ? Number(state.hatchAngle) : 20, -60, 60);
    return state;
  }
  const activeBridge = state => state.inverse ? 'off' : state.bridge;
  const bridgeConfig = state => ({ mode: state.inverse ? 'negative' : 'positive', bridge: activeBridge(state), bridgeWidth: state.lineMm, bridgeGap: state.bridgeGap, hatchAngle: state.hatchAngle });
  function artworkBand(state) {
    const bridge = activeBridge(state);
    return { top: 1 + (bridge === 'rings' ? state.lineMm : 0), bottom: PRINT_WIDTH_MM - 1 - (['bar', 'rings'].includes(bridge) ? state.lineMm : 0) };
  }
  function orderInkAllowed(format, ink) {
    return Object.hasOwn(inkNames, ink) && (format?.widthMm === 14
      || ([30, 50].includes(format?.widthMm) && ['#09090a', '#ffffff'].includes(ink)));
  }
  function createOrderDesign(format, ink, designRef) {
    const allowedWidth = { S: 14, M: 14, L: 14, Standard: 14, Wide: 30, 'Extra Wide': 50 }[format?.label];
    if (!allowedWidth || format.widthMm !== allowedWidth || !orderInkAllowed(format, ink) || !/^RML-[A-F0-9]{12}$/.test(designRef)) throw new Error('Invalid roller options.');
    return { version: 2, kind: 'custom', proofRequired: true, designRef,
      size: format.label, clicheSize: format.clicheSize, roller: format.diameterMm,
      repeatLengthMm: format.repeatLengthMm, printWidthMm: format.widthMm, ink };
  }
  function createDeferredDesign(format, ink, designRef, uploadKey) {
    const design = createOrderDesign(format, ink, designRef);
    if (!uploadKey) uploadKey = Array.from(window.crypto.getRandomValues(new Uint8Array(32)), n => n.toString(16).padStart(2, '0')).join('');
    if (!/^[a-f0-9]{64}$/.test(uploadKey)) throw new Error('A secure upload key could not be created. Please reload the page.');
    return { ...design, artworkStatus: 'awaiting-logo', uploadKey };
  }
  function orderPayload(design, variant) {
    if (!/^[1-9]\d*$/.test(variant)) throw new Error('Invalid roller variant.');
    if (!orderInkAllowed({ widthMm: design.printWidthMm }, design.ink)) throw new Error('Invalid roller ink.');
    const payload = new FormData();
    const properties = { 'Design reference': design.designRef, 'Size': design.size,
      '_rml_repeat_length_mm': design.repeatLengthMm.toFixed(1), 'Print width (mm)': String(design.printWidthMm),
      '_rml_roller_diameter_mm': String(design.roller), 'Ink': inkNames[design.ink],
      'Proof': 'Email proof approval required before production' };
    payload.set('id', variant); payload.set('quantity', '1');
    for (const [name, value] of Object.entries(properties)) payload.set(`properties[${name}]`, value);
    return payload;
  }
  function deferredPayload(design, variant) {
    if (design.artworkStatus !== 'awaiting-logo' || !/^[a-f0-9]{64}$/.test(design.uploadKey)) throw new Error('Invalid deferred order.');
    const payload = orderPayload(design, variant);
    payload.set('properties[Artwork status]', 'Awaiting upload');
    payload.set('properties[Logo delivery]', "We'll email you a secure upload link after checkout.");
    payload.set('properties[_rml_artwork_status]', 'awaiting-logo');
    payload.set('properties[_rml_upload_key]', design.uploadKey);
    return payload;
  }
  function validateQuickLogo(file) {
    if (!(file instanceof Blob) || !/\.(png|jpe?g|svg|pdf)$/i.test(file.name || '') || !['image/png', 'image/jpeg', 'image/svg+xml', 'application/pdf', 'application/octet-stream', ''].includes(file.type)) throw new Error('Choose a PNG, JPG, SVG or PDF file.');
    if (!file.size || file.size > 10 * 1024 * 1024) throw new Error('Choose a file up to 10 MB.');
    return file;
  }
  function createUploadedDesign(format, ink, designRef, file) {
    validateQuickLogo(file);
    return { ...createOrderDesign(format, ink, designRef), artworkStatus: 'uploaded', artwork: file.name };
  }
  function uploadedPayload(design, variant, file) {
    validateQuickLogo(file);
    if (design.artworkStatus !== 'uploaded' || design.artwork !== file.name) throw new Error('Choose your logo file again.');
    const payload = orderPayload(design, variant);
    payload.set('properties[Artwork status]', 'Uploaded - awaiting review');
    payload.set('properties[Logo delivery]', "We'll review your file and email a proof before production.");
    payload.set('properties[_rml_artwork_status]', 'uploaded');
    payload.set('properties[Logo file]', file, file.name);
    return payload;
  }
  function shopifyArtworkUrl(value) {
    try {
      const url = new URL(value, 'https://rollmylogo.com');
      return typeof value === 'string' && url.protocol === 'https:' && ['cdn.shopify.com', 'rollmylogo.com', 'r8uskd-88.myshopify.com'].includes(url.hostname) && url.pathname.includes('/uploads/');
    } catch { return false; }
  }
  async function addOrderItem(product, data, payload, recovery, readShopCart, shopRoot) {
    let added, confirmedCart;
    const matches = item => String(item?.variant_id || item?.id) === product.variant && item?.properties?.['Design reference'] === data.designRef && (data.artworkStatus !== 'uploaded' || shopifyArtworkUrl(item.properties?.['Logo file']));
    try {
      const response = await fetch(`${shopRoot}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: payload });
      const result = await response.json();
      if (!response.ok) {
        const error = new Error(typeof result.description === 'string' ? result.description : 'Unable to add this size to your cart. Please try again.');
        error.cartRejected = true; throw error;
      }
      added = result.items?.find(matches) || (matches(result) ? result : null);
      if (!added) throw new Error('Unconfirmed cart response.');
    } catch (error) {
      if (error.cartRejected) throw error;
      // A lost response must not create a second cart line or lose the original file.
      try { confirmedCart = await readShopCart(); added = confirmedCart.items.find(matches); } catch { /* Leave the recovery link visible. */ }
      if (!added) {
        recovery.hidden = false;
        const unconfirmed = new Error('We could not confirm the cart update. Check your cart before trying again.');
        unconfirmed.cartUnconfirmed = true; throw unconfirmed;
      }
    }
    let price = product.price;
    const cents = added.final_price ?? added.price;
    if (Number.isSafeInteger(cents) && cents >= 0 && /^[A-Z]{3}$/.test(product.currency || '')) price = new Intl.NumberFormat('en-US', { style: 'currency', currency: product.currency }).format(cents / 100);
    if (!confirmedCart) await readShopCart().catch(() => {});
    return price;
  }
  window.RollMyLogoEditor = { init, contactGaps, letterClearance, bridgeColumns, applyBridges, normalizeBridges, bridgeConfig, artworkBand, orderInkAllowed, createDeferredDesign, deferredPayload, validateQuickLogo, createUploadedDesign, uploadedPayload, shopifyArtworkUrl, addOrderItem };
  async function init(root) {
    const { customFormats } = window.RollMyLogoSizes;
    const standardDesigner = root.hasAttribute('data-standard-designer');
    const formats = standardDesigner ? { 40: customFormats[40] } : window.RollMyLogoSizes.formats;
    const sizeKeys = standardDesigner ? [40] : window.RollMyLogoSizes.keys;
    const orderOnly = root.hasAttribute('data-custom-order-only');
    if (orderOnly && window.RollMyLogoCustomOrder) { window.RollMyLogoCustomOrder.init(root); return; }
    const oldSave = root.querySelector('[data-rml-add]');
    if (!window.fabric || !window.lucide) {
      oldSave.disabled = true;
      root.querySelector('[data-rml-field-status]').textContent = 'Editor could not load. Please reload the page.';
      return;
    }
    const F = window.fabric;
    F.setFilterBackend(new F.Canvas2dFilterBackend());
    F.FabricObject.customProperties = custom;
    const fonts = Object.fromEntries(window.RollMyLogoFonts.map((f) => [f.key, f]));
    const oldFont = root.querySelector('[data-rml-font]').innerHTML;
    const helpElement = root.querySelector('.rml-design-help');
    if (orderOnly) {
      const tools = document.createElement('div'); tools.className = 'rml-custom-order-tools';
      const comparison = document.createElement('div'); comparison.dataset.customSizeGuide = '';
      tools.append(comparison);
      if (helpElement) tools.append(helpElement);
      root.querySelector('.rml-logo-choice').append(tools);
    }
    const designHelp = orderOnly ? '' : helpElement?.outerHTML || '';
    const controls = root.querySelector('.rml-controls');
    const field = root.querySelector('.rml-print-field');
    const paletteId = `rml-symbols-${++editorCount}`;
    const viewer = root.querySelector('.rml-viewer');
    if (orderOnly) viewer?.remove();
    else if (viewer) root.querySelector('.rml-canvas-wrap').after(viewer);
    field.innerHTML = `
      <div class="rml-editor-heading"><ol class="rml-editor-steps"><li>Add text, a logo or a symbol.</li><li>${standardDesigner ? 'Choose your ink color.' : 'Choose size and ink color.'}</li></ol><output data-editor-dimensions></output></div>
      <div class="rml-editor-toolbar" role="toolbar" aria-label="Layout tools">
        <span class="rml-toolbar-spacer"></span>
        ${tool('undo', 'Undo', 'undo-2')}${tool('redo', 'Redo', 'redo-2')}
        <select data-editor-zoom aria-label="Layout zoom"><option value="1">100%</option><option value="1.5">150%</option><option value="2">200%</option><option value="3">300%</option></select>
        ${tool('delete', 'Delete selection', 'trash-2')}
      </div>
      <div class="rml-editor-scroll" data-editor-scroll><div data-editor-stage><canvas data-editor-canvas></canvas><div class="rml-contact-overlay" data-contact-overlay aria-hidden="true"></div></div></div>
      <div class="rml-editor-edges"><span>0 mm / seam</span><output data-editor-end></output></div>
      <p class="rml-contact-status" data-contact-status role="status" aria-live="polite" hidden></p>
      <p class="rml-upload-status" data-rml-field-status role="status" aria-live="polite"></p>
      <div class="rml-editor-objects" data-editor-objects role="group" aria-label="Objects"></div>`;
    controls.innerHTML = `
      <div class="rml-editor-settings">
      <div class="rml-editor-content rml-control-group">
        <div class="rml-editor-toolbar rml-editor-content-tools" role="toolbar" aria-label="Add artwork">
          <button type="button" data-edit="add-text">${icon('type')}<span>Text</span></button>
          <label class="rml-editor-upload" title="PNG, JPG, WebP, SVG or single-page PDF, up to 10 MB">${icon('image-plus')}<span>Logo</span><input type="file" data-editor-upload accept="image/png,image/jpeg,image/webp,image/svg+xml,application/pdf,.pdf" aria-label="Add logo"></label>
          <button type="button" data-editor-symbol-toggle aria-expanded="false" aria-controls="${paletteId}">${icon('shapes')}<span>Symbols</span></button>
        </div>
        <div class="rml-symbol-palette" id="${paletteId}" data-editor-symbol-palette hidden role="region" aria-label="Symbols">
          <div class="rml-symbol-palette__heading"><strong>Symbols</strong><button type="button" class="rml-icon-button" data-editor-symbol-close title="Close symbols" aria-label="Close symbols">${icon('x')}</button></div>
          ${symbolGroups.map(([group, items]) => `<div class="rml-symbol-category"><h3>${group}</h3><div class="rml-symbol-grid" role="group" aria-label="${group}">${items.map(([key, name]) => `<button type="button" data-editor-symbol="${key}" title="${name}" aria-label="Add ${name.toLowerCase()}">${icon(key.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase())}</button>`).join('')}</div></div>`).join('')}
        </div>
        <div data-editor-text-controls class="rml-control-group">
          <label class="rml-control-label">Text<input data-rml-text class="rml-text-input" type="text" maxlength="128" aria-label="Selected text"></label>
          <div class="rml-font-control"><span class="rml-control-label" id="${paletteId}-font-label">Font</span><select data-rml-font class="rml-font-select" aria-label="Selected font" hidden>${oldFont}</select><button type="button" class="rml-font-trigger" data-font-open aria-haspopup="listbox" aria-expanded="false" aria-controls="${paletteId}-font-options"><span data-font-name></span>${icon('chevron-down')}</button>
            <div class="rml-font-picker" data-font-picker hidden>
              <div class="rml-font-picker__search"><label class="rml-font-search">${icon('search')}<input type="search" data-font-search aria-label="Search fonts" placeholder="Search fonts" autocomplete="off"></label><button type="button" class="rml-icon-button" data-font-close aria-label="Close font list" title="Close font list">${icon('x')}</button></div>
              <div class="rml-font-list" id="${paletteId}-font-options" data-font-list role="listbox" aria-label="Fonts">${Object.values(fonts).filter(f => f.offered !== false).map(f => `<button type="button" class="rml-font-choice" data-font-choice="${f.key}" role="option" aria-selected="false" tabindex="-1"><span class="rml-font-choice__name">${f.label || f.family}</span><span class="rml-font-choice__sample" data-font-sample aria-hidden="true"></span><span class="rml-font-choice__status" data-font-preview-status aria-hidden="true">Loading...</span>${icon('check')}</button>`).join('')}</div>
              <p class="rml-font-empty" data-font-empty role="status" hidden>No matching fonts.</p><p class="rml-font-picker-status" data-font-picker-status role="status" aria-live="polite"></p>
            </div>
          </div>
        </div>
        <label class="rml-checkbox" data-editor-logo-controls hidden><input type="checkbox" data-editor-remove-white> Remove white background</label>
      </div>
      <div class="rml-editor-order" role="toolbar" aria-label="Layer order">${tool('back', 'Send backward', 'send-to-back')}${tool('front', 'Bring forward', 'bring-to-front')}</div>
      ${standardDesigner ? '<div class="rml-control-group rml-designer-format"><strong>Standard roller</strong><span>14 mm / 0.55&Prime; print width</span><span>1 ink marker included</span></div>' : `<div class="rml-control-group"><span class="rml-control-label">Repeat size</span><div class="rml-segmented">${sizeKeys.map((d) => `<button type="button" class="rml-option" data-roller="${d}" aria-pressed="${d === 40}"><span>${formats[d].label}</span><small>${(formats[d].repeatLengthMm / 25.4).toFixed(2)}&Prime; repeat</small></button>`).join('')}</div><p class="rml-print-width">Print width: <span>0.55&Prime; / 14 mm</span></p></div>`}
      <div class="rml-control-group">
        <label class="rml-control-label" for="${paletteId}-bridge">Bridges</label>
        <select class="rml-font-select" id="${paletteId}-bridge" data-editor-bridge title="Bridges are not needed for inverse printing.">${Object.entries(bridgeLabels).map(([key, name]) => `<option value="${key}">${name}</option>`).join('')}</select>
        <div class="rml-line-thickness"><div class="rml-line-thickness__heading"><label class="rml-control-label" for="${paletteId}-line-width">Line thickness, mm</label><output for="${paletteId}-line-width" data-editor-line-width-value aria-live="off">${MIN_LINE_MM.toFixed(1)} mm</output></div><input id="${paletteId}-line-width" type="range" data-editor-line-width value="${MIN_LINE_MM}" min="${MIN_LINE_MM}" max="${MAX_LINE_MM}" step="0.1" aria-valuetext="${MIN_LINE_MM.toFixed(1)} millimeters" disabled></div>
        <div class="rml-control-group" data-editor-hatch-controls hidden>
          <div class="rml-line-thickness"><div class="rml-line-thickness__heading"><label class="rml-control-label" for="${paletteId}-bridge-gap">Stroke spacing, mm</label><output for="${paletteId}-bridge-gap" data-editor-bridge-gap-value>3.0 mm</output></div><input id="${paletteId}-bridge-gap" type="range" data-editor-bridge-gap value="3" min="1" max="10" step="0.1"></div>
          <div class="rml-line-thickness"><div class="rml-line-thickness__heading"><label class="rml-control-label" for="${paletteId}-hatch-angle">Stroke angle</label><output for="${paletteId}-hatch-angle" data-editor-hatch-angle-value>20&deg;</output></div><input id="${paletteId}-hatch-angle" type="range" data-editor-hatch-angle value="20" min="-60" max="60" step="5"></div>
        </div>
      </div>
      <div class="rml-control-group"><div class="rml-control-label"><span>Ink</span><span class="rml-ink-included">1 marker included</span></div><div class="rml-swatches">${[['#09090a', 'Black'], ['#4157ff', 'Blue'], ['#e74132', 'Red'], ['#167548', 'Green'], ['#ffffff', 'White'], ['#f2c600', 'Yellow']].map(([c, n]) => `<button type="button" class="rml-swatch" style="--swatch:${c}" data-ink="${c}" aria-label="${n} ink" title="${n} ink" aria-pressed="${n === 'Black'}"></button>`).join('')}</div>
        <div class="rml-inverse-help" data-inverse-help>
          <label class="rml-checkbox"><input type="checkbox" data-rml-inverse aria-describedby="${paletteId}-inverse-hint"> Inverse</label>
          <button type="button" class="rml-inverse-help__trigger" data-inverse-help-toggle aria-label="About inverse printing" aria-expanded="false" aria-controls="${paletteId}-inverse-hint" aria-describedby="${paletteId}-inverse-hint">${icon('info')}</button>
          <p class="rml-inverse-help__text" id="${paletteId}-inverse-hint" data-inverse-hint role="tooltip" hidden>Inverse - background prints, elements stay clean</p>
        </div>
      </div>
      <div class="rml-control-group"><span class="rml-control-label">Preview surface</span><div class="rml-segmented">${[['#c9a36a', 'Kraft'], ['#f4f1e9', 'Paper'], ['#d9d5ca', 'Fabric']].map(([c, n]) => `<button type="button" class="rml-option" data-surface="${c}" aria-pressed="${n === 'Kraft'}">${n}</button>`).join('')}</div></div>
      </div>
      <p class="rml-upload-status" data-editor-status role="status" aria-live="polite"></p>
      <div class="rml-builder-actions"><button type="button" class="rml-add" data-rml-add aria-live="polite" disabled>Add to cart</button><p class="rml-builder-proof">After checkout, we'll email your design proof. Production starts only after your approval.</p><p class="rml-microcopy" data-builder-availability role="status"></p><a class="rml-cart-recovery" data-cart-recovery hidden>Check your cart</a>${designHelp}</div>
      <dialog class="rml-cart-confirmation" data-cart-confirmation aria-labelledby="${paletteId}-cart-title" aria-describedby="${paletteId}-cart-proof">
        <button type="button" class="rml-cart-confirmation__close" data-cart-confirmation-close aria-label="Close confirmation">${icon('x')}</button>
        <div class="rml-cart-confirmation__mark" aria-hidden="true">${icon('check')}</div>
        <p class="rml-cart-confirmation__eyebrow">Added to your cart</p>
        <h2 id="${paletteId}-cart-title" tabindex="-1">Your design is saved.</h2>
        <div class="rml-cart-confirmation__artwork"><img data-cart-confirmation-image alt="Your saved roller imprint"></div>
        <h3>Custom RollMyLogo Roller</h3>
        <div class="rml-cart-confirmation__details"><span data-cart-confirmation-size></span><span class="rml-cart-confirmation__ink"><i data-cart-confirmation-swatch aria-hidden="true"></i><span data-cart-confirmation-ink></span></span><strong data-cart-confirmation-price></strong></div>
        <p class="rml-cart-confirmation__proof" id="${paletteId}-cart-proof">You will receive a digital proof by email. Production begins only after your approval.</p>
        <p class="rml-cart-confirmation__reference">Design ref: <code data-cart-confirmation-reference></code></p>
        <p class="rml-cart-confirmation__status" data-cart-confirmation-status role="alert" hidden></p>
        <div class="rml-cart-confirmation__actions"><a class="rml-cart-confirmation__checkout" data-cart-confirmation-checkout>Checkout ${icon('arrow-right')}</a><button type="button" class="rml-cart-confirmation__continue" data-cart-confirmation-continue title="Remove this item from the cart and keep editing">Keep designing</button></div>
      </dialog>
      <dialog class="rml-cart-confirmation rml-order-later" data-order-later aria-labelledby="${paletteId}-later-title" aria-describedby="${paletteId}-later-copy">
        <button type="button" class="rml-cart-confirmation__close" data-order-later-close aria-label="Close roller options">${icon('x')}</button>
        <h2 id="${paletteId}-later-title" tabindex="-1">Order now. Send your logo later.</h2>
        <p id="${paletteId}-later-copy" data-quick-copy>Your logo doesn't need to be ready today. Choose your roller and included marker color.</p>
        <div class="rml-quick-file" data-quick-file hidden>${icon('file')}<span data-quick-file-name></span><button type="button" class="rml-icon-button" data-quick-file-change aria-label="Choose another logo file" title="Choose another logo file">${icon('upload')}</button></div>
        <div class="rml-control-group"><span class="rml-control-label">Print width</span><div class="rml-segmented rml-width-options" role="group" aria-label="Order later size">${sizeKeys.map(d => `<button type="button" class="rml-option" data-later-size="${d}" aria-pressed="${d === 40}"><span>${customFormats[d].label}</span><small>${customFormats[d].widthMm} mm</small></button>`).join('')}</div></div>
        <div class="rml-control-group"><div class="rml-control-label"><span>Ink: <span data-later-ink-name>Black</span></span><span class="rml-ink-included">1 marker included</span></div><div class="rml-swatches" role="group" aria-label="Order later ink">${Object.entries(inkNames).map(([color, name]) => `<button type="button" class="rml-swatch" style="--swatch:${color}" data-later-ink="${color}" aria-label="${name} marker" title="${name} marker" aria-pressed="${name === 'Black'}"></button>`).join('')}</div></div>
        <button type="button" class="rml-add" data-order-later-add disabled>${icon('shopping-cart')}<span data-quick-add-label>Add to cart</span></button>
        <p data-order-later-status role="alert" hidden></p><a class="rml-cart-recovery" data-order-later-recovery hidden>Check your cart</a>
        <p class="rml-builder-proof" data-quick-proof>We'll email you a secure upload link after checkout. Once we receive your logo, we'll email your design proof. Production starts only after your approval.</p>
      </dialog><input type="file" data-quick-upload accept="image/png,image/jpeg,image/svg+xml,application/pdf,.png,.jpg,.jpeg,.svg,.pdf" aria-label="Upload logo for your order" hidden>`;
    if (orderOnly) {
      const shell = root.querySelector('.rml-shell');
      for (const selector of ['[data-order-later]', '[data-cart-confirmation]', '[data-quick-upload]']) shell.append(root.querySelector(selector));
    }
    window.lucide.createIcons();
    root.querySelector('.rml-preview__foot')?.remove();
    root.querySelector('.rml-seam')?.remove();
    const q = (s) => root.querySelector(s), qa = (s) => [...root.querySelectorAll(s)];
    q('[data-design-help-copy]')?.addEventListener('click', async () => {
      const address = q('[data-design-help-address]'), status = q('[data-design-help-status]');
      try {
        await navigator.clipboard.writeText(address.value);
        status.textContent = 'Email address copied.';
      } catch {
        address.focus();
        address.select();
        status.textContent = 'Select and copy the email address above.';
      }
      status.hidden = false;
    });
    const inverseHelp = q('[data-inverse-help]'), inverseHelpToggle = q('[data-inverse-help-toggle]'), inverseHint = q('[data-inverse-hint]');
    let inverseHelpPinned = false;
    const showInverseHelp = (open) => {
      inverseHint.hidden = !open;
      inverseHelpToggle.setAttribute('aria-expanded', String(open));
    };
    inverseHelp.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') showInverseHelp(true); });
    inverseHelp.addEventListener('pointerleave', () => { if (!inverseHelpPinned && !inverseHelpToggle.matches(':focus-visible')) showInverseHelp(false); });
    inverseHelpToggle.addEventListener('focus', () => { if (inverseHelpToggle.matches(':focus-visible')) showInverseHelp(true); });
    inverseHelpToggle.addEventListener('click', () => { inverseHelpPinned = !inverseHelpPinned; showInverseHelp(inverseHelpPinned); });
    inverseHelp.addEventListener('focusout', (event) => {
      if (!inverseHelp.contains(event.relatedTarget)) { inverseHelpPinned = false; showInverseHelp(false); }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !inverseHint.hidden) { inverseHelpPinned = false; showInverseHelp(false); }
    });
    document.addEventListener('pointerdown', (event) => {
      if (!inverseHelp.contains(event.target)) { inverseHelpPinned = false; showInverseHelp(false); }
    });
    const state = normalizeBridges({ diameter: 40, bridge: 'rings', lines: true, lineMm: MIN_LINE_MM, inverse: false, ink: '#09090a', surface: '#c9a36a' });
    let nextId = 1, busy = false, restoring = false, fits = false, zoom = !standardDesigner && window.matchMedia('(max-width: 960px)').matches ? 1.5 : 1, renderFrame = 0, addingToCart = false;
    q('[data-editor-zoom]').value = String(zoom);
    let history = [], historyIndex = -1;
    const loadedFonts = new Set();
    const form = root.querySelector('form.rml-builder__grid');
    const isPreview = document.body.hasAttribute('data-rml-preview');
    const shopRoot = window.Shopify?.routes?.root || '/';
    const confirmation = q('[data-cart-confirmation]');
    const laterDialog = q('[data-order-later]');
    let confirmationItem = null, revertingCart = false, confirmationDeferred = false, confirmationReturnFocus = q('[data-rml-add]');
    q('[data-cart-confirmation-checkout]').href = isPreview ? './cart.html' : `${shopRoot}checkout`;
    q('[data-cart-recovery]').href = isPreview ? './cart.html' : `${shopRoot}cart`;
    const closeConfirmation = () => { if (!revertingCart) confirmation.close(); };
    q('[data-cart-confirmation-close]').addEventListener('click', closeConfirmation);
    confirmation.addEventListener('cancel', (event) => { if (revertingCart) event.preventDefault(); });
    q('[data-cart-confirmation-checkout]').addEventListener('click', (event) => { if (revertingCart) event.preventDefault(); });
    confirmation.addEventListener('close', () => {
      confirmationItem = null;
      if (!laterDialog.open) {
        document.documentElement.classList.remove('rml-cart-confirmation-open');
        confirmationReturnFocus.focus({ preventScroll: true });
      }
    });
    confirmation.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;
      const first = q('[data-cart-confirmation-close]'), last = q('[data-cart-confirmation-continue]');
      if (event.shiftKey && [first, confirmation.querySelector('h2')].includes(document.activeElement)) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    confirmation.addEventListener('click', (event) => {
      if (event.target !== confirmation) return;
      const box = confirmation.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeConfirmation();
    });
    const showConfirmation = (data, preview, price) => {
      confirmationDeferred = data.artworkStatus === 'awaiting-logo';
      confirmationReturnFocus = q(confirmationDeferred ? '[data-choice-later]' : '[data-rml-add]') || q('[data-rml-add]');
      confirmation.querySelector('h2').textContent = confirmationDeferred ? 'Your roller is in the cart.' : 'Your design is saved.';
      q('.rml-cart-confirmation__artwork').hidden = confirmationDeferred;
      q('[data-cart-confirmation-continue]').textContent = confirmationDeferred ? 'Back to options' : 'Keep designing';
      q('[data-cart-confirmation-continue]').title = confirmationDeferred ? 'Remove this roller from the cart and change options' : 'Remove this item from the cart and keep editing';
      q('.rml-cart-confirmation__proof').textContent = confirmationDeferred
        ? "We'll email you a secure upload link after checkout. Once we receive your logo, we'll email your design proof. Production starts only after your approval."
        : 'You will receive a digital proof by email. Production begins only after your approval.';
      q('[data-cart-confirmation-status]').hidden = true;
      q('[data-cart-confirmation-status]').textContent = '';
      if (preview) q('[data-cart-confirmation-image]').src = preview;
      else q('[data-cart-confirmation-image]').removeAttribute('src');
      q('[data-cart-confirmation-size]').textContent = `${data.size} / ${data.printWidthMm} mm print width`;
      q('[data-cart-confirmation-ink]').textContent = inkNames[data.ink] || 'Custom ink';
      q('[data-cart-confirmation-swatch]').style.backgroundColor = data.ink;
      q('[data-cart-confirmation-price]').textContent = price;
      q('[data-cart-confirmation-reference]').textContent = data.designRef;
      showInverseHelp(false);
      confirmation.showModal();
      document.documentElement.classList.add('rml-cart-confirmation-open');
      confirmation.querySelector('h2').focus({ preventScroll: true });
    };
    const updateShopCart = (cart) => {
      if (!Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count)) throw new Error('Unable to read cart.');
      document.querySelectorAll('.rml-cart').forEach((link) => {
        const count = link.querySelector('strong'); if (count) count.textContent = cart.item_count;
        link.setAttribute('aria-label', `Cart with ${cart.item_count} items`);
      });
      document.dispatchEvent(new CustomEvent('rollmylogo:cart-updated', { detail: cart }));
      return cart;
    };
    const readShopCart = async () => {
      const response = await fetch(`${shopRoot}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error('Unable to read cart.');
      return updateShopCart(await response.json());
    };
    const addShopItem = (...args) => addOrderItem(...args, readShopCart, shopRoot);
    q('[data-cart-confirmation-continue]').addEventListener('click', async () => {
      if (revertingCart || !confirmationItem) return;
      const pending = confirmationItem, button = q('[data-cart-confirmation-continue]'), status = q('[data-cart-confirmation-status]');
      revertingCart = true; status.hidden = true; status.textContent = '';
      button.disabled = true; button.textContent = 'Returning to design...';
      q('[data-cart-confirmation-close]').disabled = true;
      q('[data-cart-confirmation-checkout]').setAttribute('aria-disabled', 'true');
      confirmation.setAttribute('aria-busy', 'true');
      let removed = false;
      try {
        if (isPreview) {
          const cart = JSON.parse(localStorage.getItem('rollmylogo-demo-cart') || '[]');
          if (!Array.isArray(cart)) throw new Error('Unable to read cart.');
          const index = cart.findIndex((item) => item.id === pending.id);
          if (index >= 0) {
            if (JSON.stringify(cart[index]) !== JSON.stringify(pending.saved)) throw new Error('This item changed in another window. Check your cart before trying again.');
            if (pending.previous) cart[index] = pending.previous; else cart.splice(index, 1);
            localStorage.setItem('rollmylogo-demo-cart', JSON.stringify(cart));
          }
          document.dispatchEvent(new CustomEvent('rollmylogo:cart-updated'));
        } else {
          const matches = (item) => item.properties?.['Design reference'] === pending.designRef && String(item.variant_id || item.id) === pending.variant;
          const cart = await readShopCart(), items = cart.items.filter(matches);
          if (items.length > 1 || (items.length === 1 && !items[0].key)) throw new Error('Unable to identify this design safely.');
          if (items.length === 1) {
            try {
              const response = await fetch(`${shopRoot}cart/change.js`, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ id: items[0].key, quantity: 0 }) });
              if (!response.ok) throw new Error('Unable to remove this item.');
              const updated = updateShopCart(await response.json());
              if (updated.items.some(matches)) throw new Error('This item is still in the cart.');
            } catch (error) {
              // Confirm a lost removal response without issuing a second change request.
              const updated = await readShopCart();
              if (updated.items.some(matches)) throw error;
            }
          }
        }
        removed = true;
      } catch {
        status.textContent = 'We could not confirm removal of this item. Please try Keep designing again, or check your cart.';
        status.hidden = false;
      } finally {
        revertingCart = false; button.disabled = false; button.textContent = confirmationDeferred ? 'Back to options' : 'Keep designing';
        q('[data-cart-confirmation-close]').disabled = false;
        q('[data-cart-confirmation-checkout]').removeAttribute('aria-disabled');
        confirmation.removeAttribute('aria-busy');
      }
      if (removed) { closeConfirmation(); if (confirmationDeferred) openLaterDialog(); }
    });
    const products = new Map();
    qa('[data-builder-product]').forEach((element) => {
      const { diameter, variant, priceCents, price, currency, available } = element.dataset;
      if (!sizeKeys.includes(Number(diameter))) return;
      const priced = /^\d+$/.test(priceCents || '') && Number.isSafeInteger(Number(priceCents)) && Boolean(price?.trim());
      products.set(Number(diameter), { variant: variant || '', price: priced ? price : '',
        priceCents: priced ? Number(priceCents) : null, currency,
        available: priced && /^[1-9]\d*$/.test(variant || '') && available === 'true' });
    });
    const laterState = { diameter: 40, ink: '#09090a' };
    let laterPicked = false, laterAdding = false, laterUnconfirmed = false;
    let quickMode = 'later', quickFile = null;
    q('[data-order-later-recovery]').href = isPreview ? './cart.html' : `${shopRoot}cart`;
    const syncLaterOptions = () => {
      const product = products.get(laterState.diameter), button = q('[data-order-later-add]');
      if (!orderInkAllowed(customFormats[laterState.diameter], laterState.ink)) laterState.ink = '#09090a';
      qa('[data-later-size]').forEach(element => {
        element.setAttribute('aria-pressed', String(Number(element.dataset.laterSize) === laterState.diameter));
        element.disabled = laterAdding;
      });
      qa('[data-later-ink]').forEach(element => {
        const allowed = orderInkAllowed(customFormats[laterState.diameter], element.dataset.laterInk);
        element.hidden = !allowed;
        element.setAttribute('aria-pressed', String(element.dataset.laterInk === laterState.ink));
        element.disabled = laterAdding || laterUnconfirmed || !allowed;
      });
      q('[data-later-ink-name]').textContent = inkNames[laterState.ink];
      q('[data-quick-add-label]').textContent = laterAdding ? 'Adding to cart...' : `${quickMode === 'upload' ? 'Continue to cart' : 'Add to cart'}${product?.price ? ` - ${product.price}` : ''}`;
      button.disabled = laterAdding || laterUnconfirmed || (quickMode === 'upload' && !quickFile) || (!isPreview && (!form || !product?.available));
      button.setAttribute('aria-busy', String(laterAdding));
      q('[data-order-later-close]').disabled = laterAdding;
      q('[data-quick-upload]').disabled = laterAdding || laterUnconfirmed;
      q('[data-quick-file-change]').disabled = laterAdding || laterUnconfirmed;
      q('[data-quick-file]').hidden = quickMode !== 'upload';
      q('[data-quick-file-name]').textContent = quickFile?.name || 'Choose your logo file';
    };
    function openLaterDialog(mode = 'later') {
      if (busy || laterAdding || confirmation.open || laterDialog.open) return;
      quickMode = mode;
      if (!laterPicked) { Object.assign(laterState, { diameter: state.diameter, ink: state.ink }); laterPicked = true; }
      laterDialog.querySelector('h2').textContent = mode === 'upload' ? 'Your logo. Your roller.' : 'Order now. Send your logo later.';
      q('[data-quick-copy]').textContent = mode === 'upload' ? 'Choose your roller size and included marker color. We will review your file and email a proof before production.' : "Your logo doesn't need to be ready today. Choose your roller and included marker color.";
      q('[data-quick-proof]').textContent = mode === 'upload' ? "After checkout, we'll review your logo and email your design proof. Production starts only after your approval." : "We'll email you a secure upload link after checkout. Once we receive your logo, we'll email your design proof. Production starts only after your approval.";
      if (!laterUnconfirmed) {
        const status = q('[data-order-later-status]');
        status.textContent = !isPreview && !products.get(laterState.diameter)?.available ? 'This size is currently unavailable.' : '';
        status.hidden = !status.textContent;
      }
      syncLaterOptions(); laterDialog.showModal();
      document.documentElement.classList.add('rml-cart-confirmation-open');
      laterDialog.querySelector('h2').focus({ preventScroll: true });
    }
    const closeLaterDialog = () => { if (!laterAdding) laterDialog.close(); };
    q('[data-choice-later]')?.addEventListener('click', () => openLaterDialog('later'));
    q('[data-choice-upload]')?.addEventListener('click', () => {
      if (busy || laterAdding) return;
      if (laterUnconfirmed) { openLaterDialog('upload'); return; }
      q('[data-quick-upload]').click();
    });
    q('[data-quick-file-change]').addEventListener('click', () => q('[data-quick-upload]').click());
    q('[data-quick-upload]').addEventListener('change', event => {
      const file = event.target.files?.[0]; if (!file) return;
      let error;
      try { quickFile = validateQuickLogo(file); }
      catch (reason) { quickFile = null; error = reason.message; }
      event.target.value = '';
      openLaterDialog('upload'); syncLaterOptions();
      if (!laterUnconfirmed) {
        const status = q('[data-order-later-status]');
        status.textContent = error || (!isPreview && !products.get(laterState.diameter)?.available ? 'This size is currently unavailable.' : '');
        status.hidden = !status.textContent;
      }
    });
    q('[data-order-later-close]').addEventListener('click', closeLaterDialog);
    laterDialog.addEventListener('cancel', event => { if (laterAdding) event.preventDefault(); });
    laterDialog.addEventListener('close', () => {
      if (!confirmation.open) {
        document.documentElement.classList.remove('rml-cart-confirmation-open');
        q(quickMode === 'upload' ? '[data-choice-upload]' : '[data-choice-later]')?.focus({ preventScroll: true });
      }
    });
    laterDialog.addEventListener('click', event => {
      if (event.target !== laterDialog) return;
      const box = laterDialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeLaterDialog();
    });
    qa('[data-later-size]').forEach(element => element.addEventListener('click', () => {
      laterState.diameter = Number(element.dataset.laterSize); laterPicked = true; syncLaterOptions();
      const status = q('[data-order-later-status]');
      if (!laterUnconfirmed) { status.textContent = !isPreview && !products.get(laterState.diameter)?.available ? 'This size is currently unavailable.' : ''; status.hidden = !status.textContent; }
    }));
    qa('[data-later-ink]').forEach(element => element.addEventListener('click', () => {
      if (laterAdding || laterUnconfirmed || !orderInkAllowed(customFormats[laterState.diameter], element.dataset.laterInk)) return;
      laterState.ink = element.dataset.laterInk; syncLaterOptions();
    }));
    q('[data-order-later-add]').addEventListener('click', async () => {
      if (laterAdding || laterUnconfirmed) return;
      const product = products.get(laterState.diameter), status = q('[data-order-later-status]');
      if (!isPreview && (!form || !product?.available)) { status.textContent = 'This size is currently unavailable.'; status.hidden = false; return; }
      laterAdding = true; status.hidden = true; q('[data-order-later-recovery]').hidden = true; syncLaterOptions();
      try {
        const reference = `RML-${crypto.randomUUID().replaceAll('-', '').slice(0, 12).toUpperCase()}`;
        const data = quickMode === 'upload'
          ? createUploadedDesign(customFormats[laterState.diameter], laterState.ink, reference, quickFile)
          : createDeferredDesign(customFormats[laterState.diameter], laterState.ink, reference);
        let price = product?.price || '';
        if (isPreview) {
          const cart = JSON.parse(localStorage.getItem('rollmylogo-demo-cart') || '[]');
          if (!Array.isArray(cart)) throw new Error('Unable to read cart.');
          const item = { ...data, id: data.designRef, title: 'Custom RollMyLogo Roller', quantity: 1, unitPriceCents: product?.priceCents, currency: product?.currency };
          cart.push(item); localStorage.setItem('rollmylogo-demo-cart', JSON.stringify(cart));
          confirmationItem = { id: item.id, saved: item, previous: null };
          document.dispatchEvent(new CustomEvent('rollmylogo:cart-updated'));
        } else {
          const payload = quickMode === 'upload' ? uploadedPayload(data, product.variant, quickFile) : deferredPayload(data, product.variant);
          price = await addShopItem(product, data, payload, q('[data-order-later-recovery]'));
          confirmationItem = { designRef: data.designRef, variant: product.variant };
        }
        if (quickMode === 'upload') {
          window.location.assign(isPreview ? './cart.html' : `${shopRoot}cart`);
        } else { laterDialog.close(); showConfirmation(data, null, price); }
      } catch (error) {
        laterUnconfirmed = Boolean(error.cartUnconfirmed);
        status.textContent = error.message || 'Unable to add your roller. Please try again.'; status.hidden = false;
      } finally { laterAdding = false; syncLaterOptions(); }
    });
    const editingId = document.body.hasAttribute('data-rml-preview') ? new URLSearchParams(location.search).get('design') : null;
    const stage = q('[data-editor-stage]'), scroll = q('[data-editor-scroll]');
    const canvas = new F.Canvas(q('[data-editor-canvas]'), {
      selection: true, preserveObjectStacking: true, enablePointerEvents: true,
      uniformScaling: true, selectionKey: 'shiftKey', stopContextMenu: true,
      fireRightClick: false, backgroundColor: '#f7f8fa',
    });
    canvas.upperCanvasEl.tabIndex = 0;
    canvas.upperCanvasEl.setAttribute('aria-label', 'Print layout. Select and move objects. Arrow keys move selection.');
    canvas.upperCanvasEl.setAttribute('role', 'application');
    const wheelMask = document.createElement('canvas'), artwork = document.createElement('canvas'), colored = document.createElement('canvas'), inkLayer = document.createElement('canvas'), bridgeLayer = document.createElement('canvas');
    const imprint = q('[data-rml-canvas]'), print = imprint.getContext('2d');
    const motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
    const motion = { enabled: !motionPreference.matches, visible: !window.IntersectionObserver, ready: false, frame: 0, last: 0, phase: 0, width: 1, height: 1, ratio: 1 };
    const motionToggle = document.createElement('button');
    motionToggle.type = 'button'; motionToggle.className = 'rml-imprint-toggle'; motionToggle.dataset.imprintToggle = '';
    q('.rml-preview__bar').append(motionToggle);
    const paperStyle = (context, width, height) => state.surface === '#c9a36a' && window.RollMyLogoKraft
      ? window.RollMyLogoKraft.style(context, 0, 0, width, height) : state.surface;
    const motionPlaying = () => motion.enabled && motion.visible && motion.ready && !document.hidden && root.isConnected;
    function drawImprint() {
      const repeatWidth = circumference() / PX * 3, printHeight = PRINT_WIDTH_MM * 3;
      print.setTransform(motion.ratio, 0, 0, motion.ratio, 0, 0);
      print.fillStyle = motion.paper; print.fillRect(0, 0, motion.width, motion.height);
      // Include the preceding repeat so the strip remains seamless at both edges.
      let x = 20 - motion.phase % repeatWidth;
      if (x > 0) x -= repeatWidth;
      for (; x < motion.width; x += repeatWidth) print.drawImage(inkLayer, x, (motion.height - printHeight) / 2, repeatWidth, printHeight);
    }
    function resizeImprint() {
      const rect = imprint.getBoundingClientRect();
      motion.width = Math.max(1, rect.width); motion.height = Math.max(1, rect.height);
      motion.ratio = Math.min(window.devicePixelRatio || 1, 2);
      const width = Math.round(motion.width * motion.ratio), height = Math.round(motion.height * motion.ratio);
      if (imprint.width !== width) imprint.width = width;
      if (imprint.height !== height) imprint.height = height;
      motion.paper = paperStyle(print, motion.width, motion.height);
      if (motion.ready) drawImprint();
    }
    function animateImprint(time) {
      motion.frame = 0;
      if (!motionPlaying()) { updateMotion(); return; }
      if (motion.last) motion.phase = (motion.phase + Math.min(time - motion.last, 64) * .036) % (circumference() / PX * 3);
      motion.last = time;
      drawImprint(); motion.frame = requestAnimationFrame(animateImprint);
    }
    function updateMotion() {
      const label = motion.enabled ? 'Pause imprint preview' : 'Play imprint preview';
      if (motionToggle.title !== label) {
        motionToggle.title = label; motionToggle.setAttribute('aria-label', label);
        const glyph = window.lucide.createElement(motion.enabled ? window.lucide.Pause : window.lucide.Play);
        glyph.setAttribute('aria-hidden', 'true'); motionToggle.replaceChildren(glyph);
      }
      const playing = motionPlaying(); imprint.dataset.motion = playing ? 'playing' : 'paused';
      if (playing && !motion.frame) { motion.last = 0; motion.frame = requestAnimationFrame(animateImprint); }
      else if (!playing) { cancelAnimationFrame(motion.frame); motion.frame = 0; motion.last = 0; }
    }
    motionToggle.addEventListener('click', () => { motion.enabled = !motion.enabled; updateMotion(); });
    motionPreference.addEventListener('change', () => { motion.enabled = !motionPreference.matches; updateMotion(); });
    document.addEventListener('visibilitychange', updateMotion);
    window.addEventListener('pagehide', () => { cancelAnimationFrame(motion.frame); motion.frame = 0; motion.last = 0; });
    window.addEventListener('pageshow', updateMotion);
    if (window.IntersectionObserver) new IntersectionObserver(([entry]) => {
      motion.visible = entry.isIntersecting; updateMotion();
    }).observe(imprint);
    new ResizeObserver(resizeImprint).observe(imprint);
    updateMotion();
    const format = () => formats[state.diameter];
    const circumference = () => format().repeatLengthMm * PX;
    const factor = () => { const b = artworkBand(state); return (b.bottom - b.top) / (PRINT_WIDTH_MM - 2); };
    const limits = () => { const b = artworkBand(state); return { left: 12, right: circumference() - 12, top: b.top * PX, bottom: b.bottom * PX }; };
    const artworkCenter = () => { const b = limits(); return (b.top + b.bottom) / 2; };
    const fitBridgeBand = (old) => {
      const next = limits(), ratio = (next.bottom - next.top) / (old.bottom - old.top);
      if (ratio === 1 && next.top === old.top) return;
      canvas.discardActiveObject();
      canvas.getObjects().forEach((o) => {
        o.set({ scaleX: o.scaleX * ratio, scaleY: o.scaleY * ratio, top: next.top + (o.top - old.top) * ratio }); o.setCoords();
      });
    };
    const selected = () => canvas.getActiveObject();
    const visit = (o, fn) => { if (o.getObjects) o.getObjects().forEach((child) => visit(child, fn)); else fn(o); };
    const leaves = () => { const result = []; canvas.getObjects().forEach((o) => visit(o, (v) => result.push(v))); return result; };
    const textObject = (o) => o?.rmlKind === 'text';
    const settings = { originX: 'center', originY: 'center', cornerColor: '#167a75', borderColor: '#167a75', cornerStyle: 'circle', transparentCorners: false, cornerSize: 10, touchCornerSize: 28, padding: 4, lockScalingFlip: true };
    const decorate = (o) => {
      const center = o.getRelativeCenterPoint();
      o.set(settings); o.setControlsVisibility({ ml: false, mr: false, mt: false, mb: false });
      o.setPositionByOrigin(center, 'center', 'center');
      if (!o.rmlId) o.rmlId = nextId++;
      if (o.getObjects) o.getObjects().forEach(decorate);
      o.setCoords(); return o;
    };
    const notify = (message = '', error = false) => { const node = q('[data-editor-status]'); node.textContent = message; node.dataset.error = String(error); };
    const snapshot = () => JSON.stringify({ state, scene: canvas.toObject(custom) });
    const commit = () => {
      if (restoring) return;
      const data = snapshot();
      if (history[historyIndex] !== data) { history = history.slice(0, historyIndex + 1); history.push(data); if (history.length > 60) history.shift(); historyIndex = history.length - 1; }
      sync(); schedule();
    };
    const run = async (operation) => {
      if (busy) return;
      busy = true; canvas.upperCanvasEl.style.pointerEvents = 'none'; sync();
      try { await operation(); notify(); }
      catch (error) { notify(error.message || 'Unable to update the design.', true); }
      finally { busy = false; canvas.upperCanvasEl.style.pointerEvents = ''; sync(); schedule(); }
    };
    const fontLoads = new Map();
    const loadFont = async (key) => {
      if (!fonts[key]) throw new Error('Choose a font from the catalog.');
      if (loadedFonts.has(key)) return;
      const f = fonts[key];
      if (!fontLoads.has(key)) fontLoads.set(key, document.fonts.load(`${f.style || 'normal'} ${f.weight} 58px "RML ${f.family}"`, 'RollMyLogo').then(result => {
        if (!result.length) throw new Error('Font could not load. Choose another font or reload.');
        loadedFonts.add(key);
      }).catch(error => { fontLoads.delete(key); throw error; }));
      await fontLoads.get(key);
    };
    const fontPicker = q('[data-font-picker]'), fontControl = q('.rml-font-control'), fontTrigger = q('[data-font-open]'), fontSearch = q('[data-font-search]');
    const fontChoices = qa('[data-font-choice]');
    const specimen = f => {
      const text = q('[data-rml-text]').value.trim() || 'ROLLMYLOGO';
      return !f.cyrillic && /[\u0400-\u052f]/u.test(text) ? 'ROLLMYLOGO' : text;
    };
    const styleSpecimen = (node, f) => {
      node.style.fontFamily = `"RML ${f.family}", sans-serif`;
      node.style.fontWeight = String(f.weight); node.style.fontStyle = f.style || 'normal';
      node.textContent = specimen(f);
    };
    const syncFontPicker = () => {
      const key = q('[data-rml-font]').value, f = fonts[key];
      if (!f) return;
      q('[data-font-name]').textContent = f.label || f.family;
      fontTrigger.setAttribute('aria-label', `Choose font: ${f.label || f.family}`);
      fontTrigger.disabled = busy;
      for (const row of fontChoices) row.setAttribute('aria-selected', String(row.dataset.fontChoice === key));
    };
    const previewFont = async row => {
      const f = fonts[row.dataset.fontChoice], sample = row.querySelector('[data-font-sample]'), status = row.querySelector('[data-font-preview-status]');
      styleSpecimen(sample, f);
      if (row.dataset.previewLoading === 'true') return;
      row.dataset.previewLoading = 'true';
      try { await loadFont(f.key); row.dataset.previewReady = 'true'; status.hidden = true; }
      catch { row.dataset.previewReady = 'false'; status.hidden = false; status.textContent = 'Preview unavailable'; }
      finally { row.dataset.previewLoading = 'false'; }
    };
    // Only visible rows fetch their font; searching does not preload the whole catalog.
    const fontObserver = new IntersectionObserver(entries => {
      for (const entry of entries) if (entry.isIntersecting && !fontPicker.hidden && !entry.target.hidden) previewFont(entry.target);
    }, { root: q('[data-font-list]'), rootMargin: '80px' });
    const visibleFonts = () => fontChoices.filter(row => !row.hidden);
    const setFontFocus = row => {
      fontChoices.forEach(choice => { choice.tabIndex = choice === row ? 0 : -1; });
      row?.focus({ preventScroll: true }); row?.scrollIntoView({ block: 'nearest' });
    };
    const filterFonts = () => {
      fontObserver.disconnect();
      const query = fontSearch.value.trim().toLowerCase();
      for (const row of fontChoices) {
        const f = fonts[row.dataset.fontChoice];
        row.hidden = !`${f.label || f.family} ${f.group}`.toLowerCase().includes(query);
        if (!row.hidden) {
          const sample = row.querySelector('[data-font-sample]');
          sample.textContent = specimen(f);
          if (loadedFonts.has(f.key)) styleSpecimen(sample, f);
          fontObserver.observe(row);
        }
      }
      const visible = visibleFonts(); q('[data-font-empty]').hidden = visible.length > 0;
      visible.forEach((row, i) => { row.tabIndex = i === 0 ? 0 : -1; });
    };
    const showFontPicker = (open, focusSearch = false, restoreFocus = false) => {
      fontPicker.hidden = !open; fontTrigger.setAttribute('aria-expanded', String(open));
      if (open) {
        fontSearch.value = ''; q('[data-font-picker-status]').textContent = ''; filterFonts();
        const current = fontChoices.find(row => row.dataset.fontChoice === q('[data-rml-font]').value);
        current?.scrollIntoView({ block: 'nearest' }); fontPicker.scrollIntoView({ block: 'nearest' });
        if (focusSearch) fontSearch.focus({ preventScroll: true });
      } else {
        fontObserver.disconnect();
        if (restoreFocus) fontTrigger.focus({ preventScroll: true });
      }
    };
    fontTrigger.addEventListener('click', event => { if (!busy) showFontPicker(fontPicker.hidden, event.detail === 0); });
    fontTrigger.addEventListener('keydown', event => {
      if (!busy && ['ArrowDown', 'ArrowUp'].includes(event.key)) { event.preventDefault(); showFontPicker(true, true); }
    });
    q('[data-font-close]').addEventListener('click', () => showFontPicker(false, false, true));
    document.addEventListener('pointerdown', event => { if (!fontPicker.hidden && !fontControl.contains(event.target)) showFontPicker(false); });
    fontControl.addEventListener('focusout', event => { if (!fontPicker.hidden && !fontControl.contains(event.relatedTarget)) showFontPicker(false); });
    fontControl.addEventListener('keydown', event => {
      if (event.key === 'Escape' && !fontPicker.hidden) { event.preventDefault(); event.stopPropagation(); showFontPicker(false, false, true); }
    });
    fontSearch.addEventListener('input', filterFonts);
    fontSearch.addEventListener('keydown', event => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); const visible = visibleFonts(); setFontFocus(event.key === 'ArrowDown' ? visible[0] : visible.at(-1)); }
    });
    q('[data-font-list]').addEventListener('keydown', event => {
      const row = event.target.closest('[data-font-choice]'), visible = visibleFonts(), index = visible.indexOf(row);
      if (index < 0) return;
      let next;
      if (event.key === 'ArrowDown') next = visible[(index + 1) % visible.length];
      if (event.key === 'ArrowUp') next = visible[(index - 1 + visible.length) % visible.length];
      if (event.key === 'Home') next = visible[0]; if (event.key === 'End') next = visible.at(-1);
      if (next) { event.preventDefault(); setFontFocus(next); }
    });
    fontChoices.forEach(row => row.addEventListener('click', async () => {
      if (busy) return;
      const o = selected(); if (!textObject(o)) { showFontPicker(false); return; }
      const key = row.dataset.fontChoice;
      q('[data-font-picker-status]').textContent = ''; fontPicker.setAttribute('aria-busy', 'true');
      await run(async () => {
        try {
          await loadFont(key);
          o.set({ fontKey: key, fontFamily: `RML ${fonts[key].family}`, fontWeight: fonts[key].weight, fontStyle: fonts[key].style || 'normal' });
          o.setCoords(); commit(); showFontPicker(false, false, true);
        } catch (error) { q('[data-font-picker-status]').textContent = error.message; throw error; }
      });
      fontPicker.removeAttribute('aria-busy');
    }));
    const label = (o) => o.rmlKind === 'text' ? o.text || 'Empty text' : o.filename || (o.rmlKind === 'symbol' ? 'Symbol' : 'Group');
    function sync() {
      const o = selected(), multiple = o?.isType('ActiveSelection');
      q('[data-editor-text-controls]').hidden = !textObject(o);
      q('[data-editor-logo-controls]').hidden = o?.rmlKind !== 'logo';
      if (!busy) q('[data-editor-remove-white]').checked = o?.removeWhite ?? true;
      q('[data-editor-remove-white]').disabled = busy;
      if (textObject(o) && !busy) {
        const select = q('[data-rml-font]');
        if (fonts[o.fontKey] && ![...select.options].some(option => option.value === o.fontKey)) {
          const legacyOption = new Option(fonts[o.fontKey].label || fonts[o.fontKey].family, o.fontKey);
          legacyOption.hidden = true; select.add(legacyOption);
        }
        q('[data-rml-text]').value = o.text; select.value = o.fontKey;
      }
      syncFontPicker();
      qa('[data-edit]').forEach((button) => {
        const action = button.dataset.edit;
        button.disabled = busy || (['undo', 'redo'].includes(action) ? action === 'undo' ? historyIndex <= 0 : historyIndex >= history.length - 1
          : action === 'split' ? !textObject(o) || [...o.text].length < 2
          : action === 'group' ? !multiple : action === 'ungroup' ? !o?.isType('Group')
          : ['add-text', 'select-all'].includes(action) ? false : !o);
      });
      qa('[data-roller]').forEach((b) => { b.disabled = busy; b.setAttribute('aria-pressed', String(Number(b.dataset.roller) === state.diameter)); });
      for (const key of ['ink', 'surface']) qa(`[data-${key}]`).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset[key] === state[key])));
      q('[data-editor-bridge]').value = state.bridge; q('[data-rml-inverse]').checked = state.inverse;
      q('[data-editor-bridge]').disabled = busy || state.inverse;
      q('[data-editor-bridge]').title = state.inverse ? 'Bridges are not needed for inverse printing.' : '';
      q('[data-editor-line-width]').value = state.lineMm; q('[data-editor-line-width]').disabled = busy || activeBridge(state) === 'off';
      q('[data-editor-line-width]').setAttribute('aria-valuetext', `${state.lineMm.toFixed(1)} millimeters`);
      q('[data-editor-line-width-value]').textContent = `${state.lineMm.toFixed(1)} mm`;
      q('[data-editor-hatch-controls]').hidden = activeBridge(state) !== 'hatch';
      q('[data-editor-bridge-gap]').value = state.bridgeGap;
      q('[data-editor-bridge-gap-value]').textContent = `${state.bridgeGap.toFixed(1)} mm`;
      q('[data-editor-bridge-gap]').setAttribute('aria-valuetext', `${state.bridgeGap.toFixed(1)} millimeters`);
      q('[data-editor-hatch-angle]').value = state.hatchAngle;
      q('[data-editor-hatch-angle-value]').textContent = `${state.hatchAngle}\u00b0`;
      q('[data-editor-hatch-angle]').setAttribute('aria-valuetext', `${state.hatchAngle} degrees`);
      for (const s of ['[data-editor-bridge-gap]', '[data-editor-hatch-angle]']) q(s).disabled = busy || activeBridge(state) !== 'hatch';
      for (const s of ['[data-rml-text]', '[data-rml-font]', '[data-editor-upload]', '[data-editor-symbol-toggle]', '[data-rml-inverse]']) q(s).disabled = busy;
      for (const s of ['[data-choice-upload]', '[data-choice-later]']) { const button = q(s); if (button) button.disabled = busy || !root.rmlEditor?.ready; }
      qa('[data-editor-symbol]').forEach((button) => { button.disabled = busy; });
      const product = products.get(state.diameter), addButton = q('[data-rml-add]');
      const buttonLabel = addingToCart ? 'Adding to cart...' : `Add to cart${product?.price ? ` - ${product.price}` : ''}`;
      if (addButton.textContent !== buttonLabel) addButton.textContent = buttonLabel;
      addButton.disabled = busy || !fits || (!isPreview && (!form || !product?.available));
      addButton.setAttribute('aria-busy', String(addingToCart));
      const availability = q('[data-builder-availability]');
      const availabilityLabel = isPreview || product?.available ? '' : product?.price ? 'This size is currently unavailable.' : 'Price to be confirmed';
      if (availability.textContent !== availabilityLabel) availability.textContent = availabilityLabel;
      availability.hidden = !availabilityLabel;
      const variantInput = form?.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = product?.available ? product.variant : '';
      const diameterInput = q('[data-builder-diameter-value]');
      if (diameterInput) diameterInput.value = format().diameterMm;
      const list = q('[data-editor-objects]'); list.replaceChildren();
      const selection = canvas.getActiveObjects();
      canvas.getObjects().forEach((object) => {
        const button = document.createElement('button'); button.type = 'button';
        button.textContent = label(object); button.title = label(object); button.setAttribute('aria-pressed', String(selection.includes(object)));
        button.disabled = busy;
        button.addEventListener('click', (e) => {
          let objects = e.shiftKey ? [...canvas.getActiveObjects()] : [];
          objects = objects.includes(object) ? objects.filter((item) => item !== object) : [...objects, object];
          canvas.discardActiveObject();
          if (objects.length) canvas.setActiveObject(objects.length === 1 ? object : new F.ActiveSelection(objects, { canvas, ...settings }));
          sync(); canvas.requestRenderAll();
        });
        list.append(button);
      });
    }
    function resize() {
      const width = Math.max(240, scroll.clientWidth) * zoom;
      stage.style.width = `${width}px`;
      canvas.setDimensions({ width, height: 240 });
      const maxRepeat = Math.max(...sizeKeys.map(key => formats[key].repeatLengthMm));
      const scale = Math.max(.12, (width - 48) / (maxRepeat * PX));
      canvas.setViewportTransform([scale, 0, 0, scale, 24, (240 - HEIGHT * scale) / 2]);
      canvas.requestRenderAll(); schedule();
    }
    function guides() {
      const ctx = canvas.getContext(), v = canvas.viewportTransform, s = v[0], x = v[4], y = v[5], b = limits();
      ctx.save(); ctx.strokeStyle = '#8b9098'; ctx.lineWidth = 1;
      ctx.strokeRect(x, y, circumference() * s, HEIGHT * s);
      ctx.setLineDash([4, 4]); ctx.strokeStyle = fits ? '#167a75' : '#b83232';
      ctx.strokeRect(x + b.left * s, y + b.top * s, (b.right - b.left) * s, (b.bottom - b.top) * s);
      ctx.setLineDash([]); ctx.font = '10px Arial'; ctx.fillStyle = '#626770';
      for (let mm = 0; mm <= circumference() / PX; mm += 10) {
        const at = x + mm * PX * s; ctx.fillRect(at, y - 7, 1, 5);
        if (mm % (s < .4 ? 40 : 20) === 0) ctx.fillText(String(mm), at, y - 12);
      }
      if (activeBridge(state) !== 'off' && bridgeLayer.width) ctx.drawImage(bridgeLayer, x, y, circumference() * s, HEIGHT * s);
      ctx.restore();
    }
    const recolor = () => {
      leaves().forEach((o) => {
        if (o.isType('Image')) {
          o.filters = [new F.filters.BlendColor({ color: state.inverse ? '#ffffff' : '#000000', mode: 'tint', alpha: 1 })]; o.applyFilters();
        } else { if (o.fill) o.set('fill', state.inverse ? '#fff' : '#000'); if (o.stroke) o.set('stroke', state.inverse ? '#fff' : '#000'); }
        o.dirty = true;
      });
      canvas.getObjects().forEach((o) => { o.dirty = true; });
      canvas.backgroundImage = new F.Rect({ left: 0, top: 0, width: circumference(), height: HEIGHT, fill: state.inverse ? '#000' : '#fff', strokeWidth: 0, excludeFromExport: true });
    };
    function checkContact(designPixels, actualPixels) {
      const binary = (pixels) => {
        const mask = new Uint8Array(wheelMask.width * wheelMask.height);
        for (let i = 0; i < mask.length; i++) mask[i] = pixels.data[i * 4] > 127 ? 1 : 0;
        return mask;
      };
      const dimensions = { w: wheelMask.width, h: wheelMask.height, ppm: wheelMask.width / (circumference() / PX) };
      const actual = contactGaps({ ...dimensions, mask: binary(actualPixels) });
      const original = state.inverse ? actual : contactGaps({ ...dimensions, mask: binary(designPixels) });
      const overlay = q('[data-contact-overlay]'), status = q('[data-contact-status]');
      overlay.replaceChildren();
      const [scale, , , , left, top] = canvas.viewportTransform;
      const hasArtwork = leaves().some(o => !textObject(o) || o.text.trim());
      if (hasArtwork) for (const gap of actual.gaps) for (const [a, b] of gap.parts) {
        const zone = document.createElement('div'); zone.className = 'rml-contact-gap';
        zone.dataset.gapMm = gap.length.toFixed(2); zone.dataset.seam = String(gap.wrap);
        Object.assign(zone.style, { left: `${left + a * PX * scale}px`, top: `${top}px`, width: `${(b - a) * PX * scale}px`, height: `${HEIGHT * scale}px` });
        overlay.append(zone);
      }
      const danger = hasArtwork && actual.gaps.length > 0;
      const message = !hasArtwork || !original.gaps.length ? '' : actual.empty
        ? 'No printing surface supports the marker. Adjust your design or add bridges.' : danger
        ? `Gaps exceed ${CONTACT_LIMIT_MM} mm (0.16"). Move elements closer or adjust bridges. Longest gap: ${actual.longest.toFixed(1)} mm.${actual.gaps.some(gap => gap.wrap) ? ' Includes the repeat seam.' : ''}`
        : `Gaps over ${CONTACT_LIMIT_MM} mm (0.16") are supported by the ${bridgeLabels[activeBridge(state)].toLowerCase()}.`;
      status.hidden = !message; status.dataset.error = String(danger);
      if (status.textContent !== message) status.textContent = message;
    }
    function render() {
      renderFrame = 0;
      const objects = canvas.getObjects(), b = limits();
      const invalid = objects.filter((o) => { const r = o.getBoundingRect(); return r.left < b.left - .01 || r.top < b.top - .01 || r.left + r.width > b.right + .01 || r.top + r.height > b.bottom + .01; });
      const invalidFonts = leaves().filter((o) => textObject(o) && (!loadedFonts.has(o.fontKey) || (!fonts[o.fontKey].cyrillic && /[\u0400-\u052f]/u.test(o.text))));
      fits = objects.length > 0 && invalid.length === 0 && invalidFonts.length === 0 && leaves().some((o) => !textObject(o) || o.text.trim());
      q('[data-rml-field-status]').textContent = invalidFonts.length ? 'A text object uses an unavailable font or unsupported Cyrillic. Choose Montserrat, Oswald or Rubik.' : invalid.length ? `${invalid.length} object(s) outside the print area. Move or resize them before saving.` : objects.length ? '' : 'Add text, a symbol or a logo.';
      q('[data-rml-field-status]').dataset.error = String(!fits && objects.length > 0);
      const length = circumference() / PX;
      q('[data-editor-dimensions]').textContent = standardDesigner ? 'Standard / 14 mm print width' : `${format().label} · ${length.toFixed(1)} × 14 mm`;
      q('[data-editor-end]').textContent = `${length.toFixed(1)} mm / seam`;
      q('[data-rml-roller-output]').textContent = standardDesigner ? 'Standard / 14 mm' : `${length.toFixed(1)} mm / turn`;
      // Render the same Fabric objects without selection handles, rulers or guides.
      artwork.width = wheelMask.width = 1024; artwork.height = wheelMask.height = 160;
      const ctx = artwork.getContext('2d');
      ctx.scale(artwork.width / circumference(), artwork.height / HEIGHT);
      objects.forEach((o) => o.render(ctx));
      const tint = artwork.getContext('2d'); tint.setTransform(1, 0, 0, 1, 0, 0);
      tint.globalCompositeOperation = 'source-in'; tint.fillStyle = state.inverse ? '#000' : '#fff'; tint.fillRect(0, 0, artwork.width, artwork.height);
      const mask = wheelMask.getContext('2d'); mask.fillStyle = state.inverse ? '#fff' : '#000'; mask.fillRect(0, 0, wheelMask.width, wheelMask.height); mask.drawImage(artwork, 0, 0);
      const designPixels = mask.getImageData(0, 0, wheelMask.width, wheelMask.height);
      bridgeLayer.width = wheelMask.width; bridgeLayer.height = wheelMask.height;
      const bridgeCtx = bridgeLayer.getContext('2d'), bridgePixels = bridgeCtx.createImageData(bridgeLayer.width, bridgeLayer.height);
      if (activeBridge(state) !== 'off') {
        const raw = new Uint8Array(wheelMask.width * wheelMask.height), bridges = new Uint8Array(raw.length);
        for (let i = 0; i < raw.length; i++) raw[i] = designPixels.data[i * 4] > 127 ? 1 : 0;
        const dimensions = { mask: raw, w: wheelMask.width, h: wheelMask.height, ppm: wheelMask.width / length };
        const gaps = contactGaps(dimensions), clearance = letterClearance(contactGaps(dimensions, .25).gaps, CONTACT_LIMIT_MM);
        const fillable = gaps.empty ? new Uint8Array(wheelMask.width) : bridgeColumns(wheelMask.width, dimensions.ppm, gaps.gaps, clearance);
        applyBridges(bridges, wheelMask.width, wheelMask.height, dimensions.ppm, bridgeConfig(state), fillable, wheelMask.height / PRINT_WIDTH_MM);
        const printable = mask.getImageData(0, 0, wheelMask.width, wheelMask.height);
        for (let i = 0; i < bridges.length; i++) if (bridges[i]) {
          printable.data.fill(255, i * 4, i * 4 + 4); bridgePixels.data[i * 4 + 3] = 255;
        }
        mask.putImageData(printable, 0, 0);
      }
      bridgeCtx.putImageData(bridgePixels, 0, 0);
      colored.width = inkLayer.width = wheelMask.width; colored.height = inkLayer.height = wheelMask.height;
      const pixels = mask.getImageData(0, 0, wheelMask.width, wheelMask.height);
      checkContact(designPixels, pixels);
      const ink = [1, 3, 5].map((i) => parseInt(state.ink.slice(i, i + 2), 16));
      for (let i = 0; i < pixels.data.length; i += 4) { pixels.data[i + 3] = pixels.data[i]; for (let k = 0; k < 3; k++) pixels.data[i + k] = ink[k]; }
      inkLayer.getContext('2d').putImageData(pixels, 0, 0);
      const repeatWidth = length * 3, printHeight = PRINT_WIDTH_MM * 3;
      const thumbnail = colored.getContext('2d');
      thumbnail.save(); thumbnail.scale(colored.width / repeatWidth, colored.height / printHeight);
      thumbnail.fillStyle = paperStyle(thumbnail, repeatWidth, printHeight); thumbnail.fillRect(0, 0, repeatWidth, printHeight); thumbnail.restore();
      thumbnail.drawImage(inkLayer, 0, 0);
      motion.ready = true; resizeImprint(); updateMotion();
      root.rmlDesign = { mask: wheelMask, diameter: format().diameterMm, size: format().label, sizeKey: state.diameter, innerRadiusMm: format().innerRadiusMm, printWidthMm: PRINT_WIDTH_MM, lines: activeBridge(state) === 'rings', ...bridgeConfig(state), inverse: state.inverse, fits };
      root.dispatchEvent(new CustomEvent('rollmylogo:design', { detail: root.rmlDesign }));
      canvas.requestRenderAll(); sync();
    }
    function schedule() { if (!renderFrame) renderFrame = requestAnimationFrame(render); }
    const select = (objects) => { canvas.discardActiveObject(); if (objects.length) canvas.setActiveObject(objects.length === 1 ? objects[0] : new F.ActiveSelection(objects, { canvas, ...settings })); sync(); canvas.requestRenderAll(); };
    const add = (o) => { if (leaves().length >= MAX_OBJECTS) throw new Error('Maximum 128 objects per design.'); decorate(o); canvas.add(o); select([o]); recolor(); commit(); };
    const newText = (text, key = 'montserrat') => new F.FabricText(text, { ...settings, rmlKind: 'text', fontKey: key, fontFamily: `RML ${fonts[key].family}`, fontWeight: fonts[key].weight, fontStyle: fonts[key].style || 'normal', fontSize: 58 * factor(), left: circumference() / 2, top: artworkCenter(), fill: '#000', strokeWidth: 0 });
    const restore = async (index) => {
      if (index < 0 || index >= history.length) return;
      restoring = true;
      try {
        const data = JSON.parse(history[index]);
        canvas.discardActiveObject(); await canvas.loadFromJSON(data.scene); Object.assign(state, normalizeBridges({ ...data.state }));
        canvas.getObjects().forEach(decorate); nextId = Math.max(nextId, ...leaves().map((o) => o.rmlId + 1));
        recolor(); historyIndex = index; resize();
      } finally { restoring = false; }
    };
    const constrain = (o) => {
      const b = limits(), box = o.getBoundingRect();
      const dx = box.width <= b.right - b.left ? clamp(box.left, b.left, b.right - box.width) - box.left : 0;
      const dy = box.height <= b.bottom - b.top ? clamp(box.top, b.top, b.bottom - box.height) - box.top : 0;
      o.set({ left: o.left + dx, top: o.top + dy }); o.setCoords();
    };
    const actions = {
      'add-text': async () => { await loadFont('montserrat'); add(newText('TEXT')); },
      undo: () => restore(historyIndex - 1), redo: () => restore(historyIndex + 1),
      'select-all': () => select(canvas.getObjects()),
      delete: () => { const items = canvas.getActiveObjects(); canvas.discardActiveObject(); canvas.remove(...items); commit(); },
      duplicate: async () => { const items = [...canvas.getActiveObjects()]; let count = 0; items.forEach((o) => visit(o, () => count++)); if (leaves().length + count > MAX_OBJECTS) throw new Error('Maximum 128 objects per design.'); canvas.discardActiveObject(); const copies = await Promise.all(items.map((o) => o.clone(custom))); copies.forEach((o) => { visit(o, (v) => { v.rmlId = nextId++; }); o.rmlId = nextId++; o.set({ left: o.left + 3 * PX, top: o.top }); decorate(o); constrain(o); canvas.add(o); }); select(copies); commit(); },
      group: () => { const active = selected(); if (!active?.isType('ActiveSelection')) return; const items = active.removeAll(); canvas.discardActiveObject(); canvas.remove(...items); const group = decorate(new F.Group(items, { ...settings, rmlKind: 'group' })); canvas.add(group); select([group]); commit(); },
      ungroup: () => { const group = selected(); if (!group?.isType('Group')) return; const items = group.removeAll(); canvas.discardActiveObject(); canvas.remove(group); items.forEach((o) => { decorate(o); canvas.add(o); }); select(items); commit(); },
      split: () => {
        const o = selected(); if (!textObject(o)) return;
        const graphemes = [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(o.text)].map((part) => part.segment);
        if (leaves().length - 1 + graphemes.length > MAX_OBJECTS) throw new Error('Maximum 128 objects per design.');
        const ctx = document.createElement('canvas').getContext('2d'); ctx.font = `${o.fontStyle || 'normal'} ${o.fontWeight} ${o.fontSize}px "${o.fontFamily}"`;
        const transform = o.calcTransformMatrix(), total = ctx.measureText(o.text).width;
        let prefix = ''; const letters = [];
        for (const letter of graphemes) {
          const end = ctx.measureText(prefix + letter).width, w = ctx.measureText(letter).width;
          if (letter.trim()) {
            const center = F.util.transformPoint(new F.Point(-total / 2 + end - w / 2, 0), transform);
            const child = newText(letter, o.fontKey);
            child.set({ fontSize: o.fontSize, scaleX: o.scaleX, scaleY: o.scaleY, angle: o.angle, left: center.x, top: center.y }); decorate(child); letters.push(child);
          }
          prefix += letter;
        }
        canvas.discardActiveObject(); canvas.remove(o); canvas.add(...letters); select(letters); recolor(); commit();
      },
      back: () => { canvas.getActiveObjects().forEach((o) => canvas.sendObjectBackwards(o)); commit(); },
      front: () => { canvas.getActiveObjects().slice().reverse().forEach((o) => canvas.bringObjectForward(o)); commit(); },
    };
    for (const action of ['left', 'right', 'top', 'bottom', 'center-x', 'center-y']) actions[action] = () => {
      const o = selected(); if (!o) return;
      const b = limits(), r = o.getBoundingRect();
      const dx = action === 'left' ? b.left - r.left : action === 'right' ? b.right - r.left - r.width : action === 'center-x' ? circumference() / 2 - r.left - r.width / 2 : 0;
      const dy = action === 'top' ? b.top - r.top : action === 'bottom' ? b.bottom - r.top - r.height : action === 'center-y' ? artworkCenter() - r.top - r.height / 2 : 0;
      o.set({ left: o.left + dx, top: o.top + dy }); o.setCoords(); commit();
    };
    qa('[data-edit]').forEach((button) => button.addEventListener('click', () => run(actions[button.dataset.edit])));
    const showSymbols = (open, returnFocus = false) => {
      q('[data-editor-symbol-palette]').hidden = !open;
      q('[data-editor-symbol-toggle]').setAttribute('aria-expanded', String(open));
      if (returnFocus) q('[data-editor-symbol-toggle]').focus({ preventScroll: true });
    };
    q('[data-editor-symbol-toggle]').addEventListener('click', () => showSymbols(q('[data-editor-symbol-palette]').hidden));
    q('[data-editor-symbol-close]').addEventListener('click', () => showSymbols(false, true));
    q('[data-editor-symbol-palette]').addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); showSymbols(false, true); }
    });
    qa('[data-editor-symbol]').forEach((button) => button.addEventListener('click', () => run(async () => {
      const key = button.dataset.editorSymbol; if (!Object.hasOwn(symbols, key)) return;
      const svg = window.lucide.createElement(window.lucide[key]); svg.setAttribute('stroke-width', '3');
      const result = await F.loadSVGFromString(svg.outerHTML);
      const object = new F.Group(result.objects.filter(Boolean), { width: 24, height: 24, layoutManager: new F.LayoutManager(new F.FixedLayout()) });
      object.set({ ...settings, rmlKind: 'symbol', filename: symbols[key], left: circumference() / 2, top: artworkCenter() }); object.scaleToHeight(9 * PX * factor()); add(object);
      showSymbols(false); canvas.upperCanvasEl.focus({ preventScroll: true });
    })));
    q('[data-rml-text]').addEventListener('input', (event) => { const o = selected(); if (textObject(o)) { o.set('text', event.target.value.slice(0, 128)); o.setCoords(); commit(); } });
    q('[data-rml-font]').addEventListener('change', () => {
      const key = q('[data-rml-font]').value;
      run(async () => {
      const o = selected(); if (!textObject(o)) return;
      await loadFont(key); o.set({ fontKey: key, fontFamily: `RML ${fonts[key].family}`, fontWeight: fonts[key].weight, fontStyle: fonts[key].style || 'normal' }); o.setCoords(); commit();
      });
    });
    const updateBridges = (save = true) => {
      const old = limits();
      state.bridge = q('[data-editor-bridge]').value;
      state.lineMm = Number(q('[data-editor-line-width]').value);
      state.bridgeGap = Number(q('[data-editor-bridge-gap]').value);
      state.hatchAngle = Number(q('[data-editor-hatch-angle]').value);
      normalizeBridges(state); fitBridgeBand(old);
      if (save) commit(); else { sync(); schedule(); }
    };
    q('[data-editor-bridge]').addEventListener('change', () => updateBridges());
    // Preview while dragging; store one undo step when the slider is released.
    for (const control of ['[data-editor-line-width]', '[data-editor-bridge-gap]', '[data-editor-hatch-angle]']) {
      q(control).addEventListener('input', () => updateBridges(false));
      q(control).addEventListener('change', () => updateBridges());
    }
    q('[data-rml-inverse]').addEventListener('change', () => { const old = limits(); state.inverse = q('[data-rml-inverse]').checked; fitBridgeBand(old); recolor(); commit(); });
    qa('[data-roller]').forEach((button) => button.addEventListener('click', () => { state.diameter = Number(button.dataset.roller); recolor(); commit(); }));
    for (const key of ['ink', 'surface']) qa(`[data-${key}]`).forEach((button) => button.addEventListener('click', () => { state[key] = button.dataset[key]; commit(); }));
    q('[data-editor-zoom]').addEventListener('change', () => { zoom = Number(q('[data-editor-zoom]').value); resize(); });
    const prepareLogo = async (sourcePng, removeWhite) => {
      const image = await new Promise((resolve, reject) => { const img = new Image(); img.onload = () => resolve(img); img.onerror = () => reject(new Error('Unable to open this image.')); img.src = sourcePng; });
      const source = document.createElement('canvas'); source.width = image.naturalWidth; source.height = image.naturalHeight;
      const ctx = source.getContext('2d'); ctx.drawImage(image, 0, 0);
      const pixels = ctx.getImageData(0, 0, source.width, source.height);
      let left = source.width, top = source.height, right = -1, bottom = -1, transparent = false;
      for (let i = 0; i < pixels.data.length; i += 4) {
        const p = pixels.data;
        if (p[i + 3] < 255) transparent = true;
        if (removeWhite) p[i + 3] *= Math.min(1, (255 - Math.min(p[i], p[i + 1], p[i + 2])) / 35);
        p[i] = p[i + 1] = p[i + 2] = 0;
        if (p[i + 3] > 8) { const x = i / 4 % source.width, y = Math.floor(i / 4 / source.width); left = Math.min(left, x); top = Math.min(top, y); right = Math.max(right, x); bottom = Math.max(bottom, y); }
      }
      if (right < 0 && transparent && removeWhite) return prepareLogo(sourcePng, false);
      if (right < 0) throw new Error('The image contains no visible artwork.');
      ctx.putImageData(pixels, 0, 0);
      const cropped = document.createElement('canvas'); cropped.width = right - left + 1; cropped.height = bottom - top + 1;
      cropped.getContext('2d').drawImage(source, left, top, cropped.width, cropped.height, 0, 0, cropped.width, cropped.height);
      return { cropped, removeWhite };
    };
    q('[data-editor-remove-white]').addEventListener('change', (event) => {
      const removeWhite = event.target.checked, object = selected();
      if (object?.rmlKind !== 'logo') return;
      run(async () => {
        const { cropped, removeWhite: actual } = await prepareLogo(object.sourcePng, removeWhite);
        const height = object.height * object.scaleY;
        const replacement = await F.FabricImage.fromURL(cropped.toDataURL());
        object.setElement(replacement.getElement()); object.set({ removeWhite: actual, scaleX: height / object.height, scaleY: height / object.height }); object.setCoords(); recolor(); commit();
      });
    });
    q('[data-editor-upload]').addEventListener('change', (event) => run(async () => {
      const file = event.target.files?.[0]; if (!file) return;
      try {
        if (!/\.(png|jpe?g|webp|svg|pdf)$/i.test(file.name) || !['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml', 'application/pdf', 'application/octet-stream', ''].includes(file.type)) throw new Error('Choose a PNG, JPG, WebP, SVG or single-page PDF.');
        if (!file.size || file.size > 10 * 1024 * 1024) throw new Error('Choose a file smaller than 10 MB.');
        let source;
        if (/\.pdf$/i.test(file.name) || file.type === 'application/pdf') {
          notify('Opening PDF...');
          source = await renderPdfLogo(file);
        } else {
          const url = URL.createObjectURL(file);
          let image;
          try { image = await new Promise((resolve, reject) => { const img = new Image(); img.onload = () => resolve(img); img.onerror = () => reject(new Error('Unable to open this image.')); img.src = url; }); }
          finally { URL.revokeObjectURL(url); }
          if (!image.naturalWidth || image.naturalWidth * image.naturalHeight > 40000000) throw new Error('Export the image below 40 megapixels.');
          source = document.createElement('canvas');
          const scale = Math.min(1, 1024 / Math.max(image.naturalWidth, image.naturalHeight));
          source.width = Math.round(image.naturalWidth * scale); source.height = Math.round(image.naturalHeight * scale);
          source.getContext('2d').drawImage(image, 0, 0, source.width, source.height);
        }
        const sourcePng = source.toDataURL();
        const { cropped, removeWhite } = await prepareLogo(sourcePng, true);
        const object = await F.FabricImage.fromURL(cropped.toDataURL());
        object.set({ ...settings, rmlKind: 'logo', filename: file.name, sourcePng, removeWhite, left: circumference() / 2, top: artworkCenter() }); object.scaleToHeight(10 * PX * factor());
        add(object);
      } finally { event.target.value = ''; }
    }));
    canvas.on('after:render', guides);
    canvas.on('selection:created', sync); canvas.on('selection:updated', sync); canvas.on('selection:cleared', sync);
    canvas.on('object:moving', ({ target }) => { constrain(target); schedule(); });
    canvas.on('object:scaling', schedule); canvas.on('object:rotating', schedule); canvas.on('object:modified', commit);
    canvas.upperCanvasEl.addEventListener('pointerdown', () => canvas.upperCanvasEl.focus());
    canvas.upperCanvasEl.addEventListener('keydown', (event) => {
      if (busy) return;
      if ((event.ctrlKey || event.metaKey) && ['z', 'y', 'a'].includes(event.key.toLowerCase())) {
        event.preventDefault(); run(event.key.toLowerCase() === 'a' ? actions['select-all'] : event.key.toLowerCase() === 'y' || event.shiftKey ? actions.redo : actions.undo); return;
      }
      if (['Delete', 'Backspace'].includes(event.key)) { event.preventDefault(); run(actions.delete); return; }
      if (event.key === 'Escape') { canvas.discardActiveObject(); sync(); return; }
      const o = selected(); if (!o || !event.key.startsWith('Arrow')) return;
      event.preventDefault(); const step = event.shiftKey ? PX : .1 * PX;
      o.set({ left: o.left + (event.key === 'ArrowRight' ? step : event.key === 'ArrowLeft' ? -step : 0), top: o.top + (event.key === 'ArrowDown' ? step : event.key === 'ArrowUp' ? -step : 0) }); o.setCoords(); constrain(o); commit();
    });
    const exportDesign = () => {
      const selection = canvas.getActiveObjects();
      canvas.discardActiveObject(); render();
      const bridge = activeBridge(state);
      const design = { version: 2, kind: 'custom', size: format().label, clicheSize: format().clicheSize, roller: format().diameterMm, repeatLengthMm: circumference() / PX, printWidthMm: PRINT_WIDTH_MM, ink: state.ink, surface: state.surface, inverse: state.inverse, ...bridgeConfig(state),
        text: leaves().filter(textObject).map((o) => o.text).join(' '), artwork: leaves().filter((o) => o.rmlKind === 'logo').map((o) => o.filename).join(', '),
        rails: { continuous: ['bar', 'rings'].includes(bridge), top: bridge === 'rings', bottom: ['bar', 'rings'].includes(bridge), thicknessMm: ['bar', 'rings'].includes(bridge) ? state.lineMm : 0 },
        editor: { units: 'design-px', pixelsPerMm: PX, state: { ...state }, scene: canvas.toObject(custom) },
      };
      select(selection);
      return design;
    };
    form?.addEventListener('submit', (event) => { event.preventDefault(); q('[data-rml-add]').click(); });
    q('[data-rml-add]').addEventListener('click', () => run(async () => {
      if (confirmation.open) return;
      const product = products.get(state.diameter);
      if (!isPreview && (!form || !product?.available)) throw new Error('This size is not available to order yet.');
      const data = exportDesign(); if (!fits) throw new Error('Keep all objects inside the print area before saving.');
      const thumb = document.createElement('canvas'); thumb.width = 960; thumb.height = Math.round(960 * HEIGHT / circumference()); thumb.getContext('2d').drawImage(colored, 0, 0, thumb.width, thumb.height);
      data.designRef = `RML-${crypto.randomUUID().replaceAll('-', '').slice(0, 12).toUpperCase()}`;
      const preview = thumb.toDataURL();
      let confirmedPrice = product?.price || '';
      addingToCart = true; q('[data-cart-recovery]').hidden = true; sync();
      try {
      if (isPreview) {
        let cart; try { cart = JSON.parse(localStorage.getItem('rollmylogo-demo-cart') || '[]'); if (!Array.isArray(cart)) cart = []; } catch { cart = []; }
        const index = editingId ? cart.findIndex((item) => item.id === editingId) : -1;
        if (index >= 0 && cart[index].designRef) data.designRef = cart[index].designRef;
        const item = { ...data, id: index >= 0 ? editingId : `${Date.now()}-${nextId++}`, title: 'Custom RollMyLogo Roller', quantity: index >= 0 ? cart[index].quantity : 1, previewImage: preview, unitPriceCents: product?.priceCents, currency: product?.currency };
        const pending = { id: item.id, saved: item, previous: index >= 0 ? cart[index] : null };
        if (index >= 0) cart[index] = item; else cart.push(item);
        try { localStorage.setItem('rollmylogo-demo-cart', JSON.stringify(cart)); } catch { throw new Error('Browser storage is full. Remove an old design or use smaller logos.'); }
        confirmationItem = pending;
        document.dispatchEvent(new CustomEvent('rollmylogo:cart-updated'));
      } else if (form) {
        const payload = new FormData(form);
        const blob = await new Promise((resolve) => wheelMask.toBlob(resolve, 'image/png'));
        if (!blob) throw new Error('Unable to export the print artwork.');
        payload.set('properties[Print mask]', new File([blob], 'rollmylogo-print-mask.png', { type: 'image/png' }));
        payload.set('properties[Editable design]', new File([JSON.stringify(data)], 'rollmylogo-design.json', { type: 'application/json' }));
        payload.set('properties[Design reference]', data.designRef);
        payload.set('properties[Size]', data.size);
        payload.delete('properties[Repeat length (mm)]');
        payload.set('properties[_rml_repeat_length_mm]', data.repeatLengthMm.toFixed(1));
        payload.set('properties[Print width (mm)]', String(data.printWidthMm));
        payload.delete('properties[Roller diameter (mm)]');
        payload.set('properties[_rml_roller_diameter_mm]', String(data.roller));
        payload.set('properties[Ink]', inkNames[data.ink] || data.ink);
        payload.set('properties[Proof]', 'Email proof approval required before production');
        payload.set('properties[Bridges]', `${bridgeLabels[data.bridge]}${data.bridge !== 'off' ? ` / ${data.bridgeWidth.toFixed(1)} mm` : ''}${data.bridge === 'hatch' ? ` / ${data.bridgeGap.toFixed(1)} mm spacing / ${data.hatchAngle} degrees` : ''}`);
        const input = q('[data-rml-design-data]'); if (input) input.value = JSON.stringify({ version: 2, size: data.size, clicheSize: data.clicheSize, roller: data.roller, repeatLengthMm: data.repeatLengthMm, printWidthMm: PRINT_WIDTH_MM, objects: leaves().length, rails: data.rails, ...bridgeConfig(state) });
        if (input) payload.set(input.name, input.value);
        payload.set('id', product.variant); payload.set('quantity', '1');
        confirmedPrice = await addShopItem(product, data, payload, q('[data-cart-recovery]'));
        confirmationItem = { designRef: data.designRef, variant: product.variant };
      }
      showConfirmation(data, preview, confirmedPrice);
      root.dispatchEvent(new CustomEvent('rollmylogo:design-ready', { detail: data }));
      } finally { addingToCart = false; }
    }));
    new ResizeObserver(resize).observe(scroll);
    root.rmlEditor = { canvas, exportDesign, ready: false };
    window.RollMyLogoKraft?.ready.then(schedule);
    resize();
    if (orderOnly) { root.rmlEditor.ready = true; sync(); return; }
    await run(async () => {
      if (editingId) {
        const item = JSON.parse(localStorage.getItem('rollmylogo-demo-cart') || '[]').find((v) => v.id === editingId);
        if (!item?.editor || item.version !== 2) throw new Error('Saved design was not found in this browser.');
        const check = async (value) => {
          if (!value || typeof value !== 'object') return;
          if (value.src && !/^data:image\/png;base64,[A-Za-z0-9+/=]+$/.test(value.src)) throw new Error('This design contains an unsupported image.');
          if (value.fontKey) await loadFont(value.fontKey);
          for (const child of Object.values(value)) if (child && typeof child === 'object') await check(child);
        };
        await check(item.editor.scene);
        if (!sizeKeys.includes(item.editor.state.diameter)) throw new Error('This design uses a different roller size.');
        await canvas.loadFromJSON(item.editor.scene);
        const saved = { ...item.editor.state, bridge: item.editor.state.bridge || (item.editor.state.lines ? 'rings' : 'off') };
        const oldBand = !item.editor.state.bridge && saved.lines ? { top: 1 + (Number(saved.lineMm) || MIN_LINE_MM), bottom: PRINT_WIDTH_MM - 1 - (Number(saved.lineMm) || MIN_LINE_MM) } : artworkBand(saved);
        Object.assign(state, normalizeBridges(saved));
        fitBridgeBand({ top: oldBand.top * PX, bottom: oldBand.bottom * PX });
        canvas.getObjects().forEach(decorate); nextId = Math.max(nextId, ...leaves().map((o) => o.rmlId + 1)); recolor(); commit();
      } else {
        await loadFont('montserrat'); add(newText('ROLLMYLOGO'));
      }
      root.rmlEditor.ready = true;
    });
  }
})();
